import { useMemo } from "react";
import { fitJohnsonSu, sliderBounds } from "./priceDistribution";
import { refitWithExactMedian, refitWithExactMode } from "./constrainedFits";
import { buildQuantileCdf } from "./quantileCdf";

// The web app always requests the default zip model variant (it never sends a modelVariant
// param), whose quantile knots are fixed at p2/p25/p50/p75/p98. The area variant tops out
// at p94 — if this UI ever requests it, the levels must come from the API instead.
const ZIP_QUANTILE_LEVELS = [0.02, 0.25, 0.5, 0.75, 0.98];

// The five $/mi knots from an ApiLoadPrices payload, or null if any is missing.
// NB: the p50 knot is averageRate.average; `median` is never set on this endpoint.
const quantileKnots = (data) => {
  const q = data?.averageRate;
  if (!q) return null;
  const values = [q.minValue, q.midLow, q.average, q.midHigh, q.maxValue];
  return values.some((v) => v == null || !(v > 0)) ? null : values;
};

// Smooth Johnson SU fit for the market density curve, once per response. The fit is re-solved so
// its peak sits exactly at the p50 knot (the displayed rate, where the marker is drawn); the knots
// are otherwise fitted smoothly. Returns null when the response can't support a renderable fit
// (endpoints that don't set the midLow/midHigh knots, missing data, degenerate bounds) — callers
// fall back to the RateRangeBar.
export const useRateDistributionFit = (data) => {
  return useMemo(() => {
    const values = quantileKnots(data);
    if (!values) return null;
    try {
      const fit = refitWithExactMode(refitWithExactMedian(fitJohnsonSu(ZIP_QUANTILE_LEVELS, values)));
      const { lo, hi } = sliderBounds(fit);
      return hi > lo ? fit : null;
    } catch {
      return null;
    }
  }, [data]);
};

// Exact CDF through the quantile knots for the buy rate chart, once per response.
export const useQuantileCdf = (data) => {
  return useMemo(() => {
    const values = quantileKnots(data);
    if (!values) return null;
    const cdf = buildQuantileCdf(ZIP_QUANTILE_LEVELS, values);
    return cdf.hi > cdf.lo ? cdf : null;
  }, [data]);
};

// Linear x-scale over [lo, hi] $/mi, shared by both rate charts so the axis convention can't drift.
export const rateScale = (lo, hi, viewW) => ({
  lo,
  hi,
  toXFrac: (rpm) => (rpm - lo) / (hi - lo),
  toX: (rpm) => ((rpm - lo) / (hi - lo)) * viewW,
  sampleRpm: (i, n) => lo + ((hi - lo) * i) / n,
});
